import { useEffect, useMemo, useState, useRef } from "react";
import React from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { api } from "@/lib/api";
import { House, ArrowLeft, Camera, Sparkle, CheckCircle, XCircle, Circle, Thermometer, FilePdf, X, Play, Images } from "@phosphor-icons/react";

/**
 * Property Walk — per-unit checklist walkthrough.
 * URL: /admin/properties/:address/walk
 *
 * Flow: tap "Walk Unit" → multi-select 1-15 photos from library or camera
 *       → single POST /api/walk-reports/process runs Claude on every photo
 *       concurrently → checklist chips light green/red, hot water temp
 *       extracted, nameplate photos auto-saved to that unit's assets.
 */
const CHECKLIST = [
  { key: "hot_water_temp",      label: "Hot Water",      short: "HW" },
  { key: "blinds_screens",      label: "Blinds/Screens", short: "BL" },
  { key: "tp_holder",           label: "TP Holder",      short: "TP" },
  { key: "shower_rod",          label: "Shower Rod",     short: "SR" },
  { key: "hair_catcher",        label: "Hair Catcher",   short: "HC" },
  { key: "shower_caulk",        label: "Shower Caulk",   short: "SC" },
  { key: "cleanliness_kitchen", label: "Kitchen Clean",  short: "KC" },
  { key: "cleanliness_bath",    label: "Bath Clean",     short: "BC" },
];

export default function PropertyWalk() {
  const { address: encodedAddr } = useParams();
  const address = decodeURIComponent(encodedAddr || "");
  const [units, setUnits] = useState(null);
  const [reports, setReports] = useState({});          // unit -> latest report
  const [walkingUnit, setWalkingUnit] = useState("");  // in-flight unit
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  // Long-press-to-note state: which chip's note editor is open, plus refs
  // so we can cancel the timer if the tap ends early (= regular cycle-tap).
  const [noteEditor, setNoteEditor] = useState(null);   // {unit, key, draft}
  const pressTimer = React.useRef(null);
  const pressFired = React.useRef(false);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");
  const [showComplete, setShowComplete] = useState(true);
  const [queued, setQueued] = useState({}); // unit -> File[]  in-memory tray
  const [queueRestored, setQueueRestored] = useState(false);
  const [galleryUnit, setGalleryUnit] = useState(""); // unit currently open in the photo viewer
  const authToken = (typeof localStorage !== "undefined" && localStorage.getItem("access_token")) || "";
  const photoUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/walk-reports/photo/${encodeURI(p)}?auth=${encodeURIComponent(authToken)}`;

  // ── Queue persistence via sessionStorage ───────────────────────────
  // File objects survive back-navigation because they're serialized to
  // base64 blobs and restored on mount. Prevents the "went back and lost
  // my photos" frustration. Scoped by address so switching properties
  // doesn't clobber the other's tray.
  const idbKey = `walk-queue-${address}`;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = sessionStorage.getItem(idbKey);
        if (!raw) { setQueueRestored(true); return; }
        const parsed = JSON.parse(raw);
        const out = {};
        for (const [unit, arr] of Object.entries(parsed)) {
          out[unit] = await Promise.all(arr.map(async ({ dataUrl, name, type }) => {
            const res = await fetch(dataUrl);
            const blob = await res.blob();
            return new File([blob], name || "photo.jpg", { type: type || blob.type });
          }));
        }
        if (!cancelled) {
          setQueued(out);
          const totalCount = Object.values(out).reduce((n, q) => n + q.length, 0);
          if (totalCount > 0) {
            toast.info(`Restored ${totalCount} queued photo${totalCount === 1 ? "" : "s"} from before`,
                        { duration: 3000 });
          }
        }
      } catch (_) { /* corrupt storage — ignore */ }
      finally {
        if (!cancelled) setQueueRestored(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [address]);

  // Persist queue on every change (base64 in sessionStorage — survives back
  // navigation inside the tab; typical 5-photo iPhone tray is <5 MB).
  // GATED on queueRestored so we don't wipe the just-saved state during the
  // first render pass before restore has run.
  useEffect(() => {
    if (!queueRestored) return;
    (async () => {
      try {
        const totalCount = Object.values(queued).reduce((n, q) => n + q.length, 0);
        if (!totalCount) {
          sessionStorage.removeItem(idbKey);
          return;
        }
        const serial = {};
        for (const [unit, files] of Object.entries(queued)) {
          serial[unit] = await Promise.all(files.map(f => new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve({ dataUrl: reader.result, name: f.name, type: f.type });
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(f);
          })));
          serial[unit] = serial[unit].filter(Boolean);
        }
        sessionStorage.setItem(idbKey, JSON.stringify(serial));
      } catch (_) { /* quota exceeded — silently give up on persistence */ }
    })();
  }, [queued, idbKey, queueRestored]);

  // beforeunload guard — full-page reload / close warns when photos are queued
  useEffect(() => {
    const handler = (e) => {
      const totalCount = Object.values(queued).reduce((n, q) => n + q.length, 0);
      if (totalCount > 0) {
        e.preventDefault();
        e.returnValue = `You have ${totalCount} queued photo${totalCount === 1 ? "" : "s"} not yet processed.`;
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [queued]);

  // Append files to the per-unit tray. This is the workaround for Android's
  // flaky picker: instead of submit-on-select, we stack photos in memory so
  // the user can tap Camera repeatedly (or Gallery when it works) and only
  // submit when THEY hit Process. Camera path is 100% reliable on every
  // Android/iOS device — one shot at a time — so worst case they tap Camera
  // 5 times and get 5 photos queued.
  //
  // Each queued entry is `{ id, file, url }`. The preview `url` is created
  // ONCE via URL.createObjectURL when the file is added — NOT on every
  // render — so mobile browsers (esp. Android WebView / iOS Safari) don't
  // GC transient blobs and render broken tiles. `url` is revoked on remove /
  // clear / unmount to keep memory pressure sane on 15-photo trays.
  const queueFor = (unit) => queued[unit] || [];
  const addToQueue = (unit, files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return 0;
    const entries = list.map(f => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      file: f,
      url: URL.createObjectURL(f),
    }));
    let added = 0;
    setQueued(prev => {
      const cur = prev[unit] || [];
      const room = Math.max(0, 15 - cur.length);
      const accepted = entries.slice(0, room);
      // Revoke any that got dropped by the 15-cap so we don't leak.
      entries.slice(room).forEach(e => { try { URL.revokeObjectURL(e.url); } catch (_) {} });
      added = accepted.length;
      return { ...prev, [unit]: [...cur, ...accepted] };
    });
    return added;
  };
  const clearQueue = (unit) => setQueued(prev => {
    const cur = prev[unit] || [];
    cur.forEach(e => { try { URL.revokeObjectURL(e.url); } catch (_) {} });
    const nx = { ...prev }; delete nx[unit]; return nx;
  });
  const removeFromQueue = (unit, idx) => setQueued(prev => {
    const cur = prev[unit] || [];
    const gone = cur[idx];
    if (gone) { try { URL.revokeObjectURL(gone.url); } catch (_) {} }
    const nx = cur.filter((_, i) => i !== idx);
    if (!nx.length) { const c = { ...prev }; delete c[unit]; return c; }
    return { ...prev, [unit]: nx };
  });
  // Revoke every outstanding blob URL on unmount so leaving the page doesn't
  // leak 15 × unit_count blobs on low-end phones.
  useEffect(() => () => {
    Object.values(queued).forEach(arr => (arr || []).forEach(e => {
      try { URL.revokeObjectURL(e.url); } catch (_) {}
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      // Pull all units at this address via unified search
      const r = await api.get("/search", { params: { q: address } });
      const raw = (r.data?.results?.units || []).map(u => {
        const m = (u.path || "").match(/\/admin\/properties\/([^/]+)\/unit\/([^/]+)/);
        return {
          ...u,
          address: m ? decodeURIComponent(m[1]) : "",
          unit:    m ? decodeURIComponent(m[2]) : "",
        };
      }).filter(u => (u.address || "").toLowerCase() === address.toLowerCase() && u.unit);
      // Enrich each with unit assets + vacancy
      const detailed = await Promise.all(raw.map(async u => {
        try {
          const d = await api.get("/properties/unit-detail", { params: { address, unit: u.unit }});
          return { ...u, assets: d.data?.assets || [],
                   appliance_count: (d.data?.assets || []).length,
                   vacancy_status: d.data?.vacancy_status || "" };
        } catch { return { ...u, assets: [], appliance_count: 0 }; }
      }));
      detailed.sort((a, b) => {
        const na = parseInt((a.unit || "").match(/\d+/)?.[0] || "999", 10);
        const nb = parseInt((b.unit || "").match(/\d+/)?.[0] || "999", 10);
        return na - nb || (a.unit || "").localeCompare(b.unit || "");
      });
      setUnits(detailed);
      // Load latest walk reports for these units
      try {
        const rep = await api.get("/walk-reports", { params: { address }});
        const map = {};
        (rep.data?.reports || []).forEach(r => { map[r.unit] = r; });
        setReports(map);
      } catch (_) { /* ignore report load fails — new install */ }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load walk");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [address]);

  const walk = async (unit, files) => {
    // Unwrap {file, url, id} tray entries → File[] for FormData submission.
    // Accept plain File[] too so this function stays callable from external
    // code paths that haven't been migrated to the tray format.
    const list = Array.from(files || [])
      .map(f => (f && f.file) ? f.file : f)
      .filter(Boolean);
    if (!list.length) {
      toast.error(`No photos selected for ${unit}`);
      return;
    }
    if (list.length > 15) {
      toast.error(`Max 15 photos per walk — you selected ${list.length}`);
      return;
    }
    // Immediate feedback so the user never wonders "did anything happen?"
    toast.info(`${unit}: uploading ${list.length} photo${list.length === 1 ? "" : "s"} to Claude…`,
               { id: `walk-${unit}`, duration: 60000 });
    setWalkingUnit(unit);
    setProgress({ done: 0, total: list.length });
    try {
      const fd = new FormData();
      fd.append("address", address);
      fd.append("unit", unit);
      list.forEach(f => fd.append("files", f));
      const r = await api.post("/walk-reports/process", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      const d = r.data || {};
      const passes = (d.checklist || []).filter(c => c.status === "pass").length;
      const fails  = (d.checklist || []).filter(c => c.status === "fail").length;
      const hw     = d.hot_water_temp_f;
      toast.success(
        `${unit}: ${passes} ✅ · ${fails} ❌ · ${d.nameplates_saved} appliance${d.nameplates_saved === 1 ? "" : "s"}`
        + (hw ? ` · Water ${hw}°F` : ""),
        { id: `walk-${unit}`, duration: 6500 }
      );
      // Update the local report cache so chips light immediately
      setReports(prev => ({
        ...prev,
        [unit]: {
          id: d.id,
          unit, walked_at: d.walked_at,
          hot_water_temp_f: hw, nameplates_saved: d.nameplates_saved,
          total_photos: d.photos_processed, checklist: d.checklist,
        }
      }));
      // Refresh unit assets for the chips
      try {
        const ud = await api.get("/properties/unit-detail", { params: { address, unit }});
        setUnits(prev => (prev || []).map(u => u.unit === unit
          ? { ...u, assets: ud.data?.assets || [], appliance_count: (ud.data?.assets || []).length }
          : u
        ));
      } catch (_) {}
      // Wipe the queue only on success
      clearQueue(unit);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "unknown error";
      const code = e.response?.status;
      // Log the raw error so the user (or Emergent support) can see it in
      // the mobile browser console if the toast disappears too fast.
      console.error("[walk] failed", { unit, code, msg, err: e });
      toast.error(`${unit}: ${code ? `HTTP ${code} — ` : ""}${msg}`,
                  { id: `walk-${unit}`, duration: 8000 });
    } finally {
      setWalkingUnit("");
      setProgress({ done: 0, total: 0 });
    }
  };

  // Tap-to-override a checklist chip. Cycles pending → pass → fail → pending
  // and PATCHes /walk-reports/{id}/checklist so the override sticks server-side
  // AND is marked `overridden_by`/`overridden_at` — meaning the next AI walk
  // can't downgrade it (merge rule respects overrides).
  const overrideCheck = async (unit, key, opts = {}) => {
    const rep = reports[unit];
    if (!rep || !rep.id) {
      toast.error(`${unit}: run walk first — no report to override`, { duration: 4000 });
      return;
    }
    const cur = (rep.checklist || []).find(c => c.key === key);
    const curStatus = cur?.status || "pending";
    // Note-only save keeps status the same; cycle taps advance it.
    const next = opts.noteOnly
      ? curStatus
      : (curStatus === "pending" ? "pass" : curStatus === "pass" ? "fail" : "pending");
    const notesToSend = typeof opts.notes === "string" ? opts.notes : undefined;
    // Optimistic UI
    setReports(prev => {
      const r = prev[unit];
      if (!r) return prev;
      const cl = (r.checklist || []).map(c => c.key === key ? {
        ...c, status: next,
        ...(notesToSend !== undefined ? { notes: notesToSend } : {}),
        overridden_by: "you", overridden_at: new Date().toISOString(),
      } : c);
      const exists = cl.some(c => c.key === key);
      if (!exists) cl.push({ key, status: next,
        ...(notesToSend !== undefined ? { notes: notesToSend } : {}),
        overridden_by: "you", overridden_at: new Date().toISOString() });
      return { ...prev, [unit]: { ...r, checklist: cl } };
    });
    try {
      const body = { key, status: next };
      if (notesToSend !== undefined) body.notes = notesToSend;
      await api.patch(`/walk-reports/${rep.id}/checklist`, body);
      const summary = opts.noteOnly ? "note saved" : `→ ${next.toUpperCase()}`;
      toast.success(`${unit} · ${key.toUpperCase()} ${summary}`, {
        id: `ovr-${unit}-${key}`, duration: 2500,
      });
    } catch (e) {
      toast.error(`Override failed: ${e.response?.data?.detail || e.message}`, { duration: 5000 });
      try {
        const list = await api.get("/walk-reports", { params: { address, unit }});
        const fresh = (list.data || [])[0];
        if (fresh) setReports(prev => ({ ...prev, [unit]: fresh }));
      } catch (_) {}
    }
  };

  const visible = useMemo(() => {
    if (!units) return [];
    const needle = filter.trim().toLowerCase();
    return units.filter(u => {
      const rep = reports[u.unit];
      const done = rep && rep.checklist && rep.checklist.every(c => c.status !== "pending");
      if (!showComplete && done) return false;
      if (!needle) return true;
      return (u.unit || "").toLowerCase().includes(needle);
    });
  }, [units, filter, showComplete, reports]);

  const summary = useMemo(() => {
    if (!units) return null;
    const walked = units.filter(u => reports[u.unit]).length;
    return { total: units.length, walked,
             appliances: units.reduce((n, u) => n + u.appliance_count, 0) };
  }, [units, reports]);

  if (err) return <AdminShell><div className="p-6 text-red-700 font-bold">{err}</div></AdminShell>;

  return (
    <AdminShell>
      <div className="max-w-4xl mx-auto p-4 space-y-4" data-testid="property-walk-page">
        {/* Header */}
        <div>
          <Link to={`/admin/properties/${encodeURIComponent(address)}`}
            className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-900">
            <ArrowLeft weight="bold" size={12}/> {address}
          </Link>
          <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-1 flex items-center gap-2">
            <House weight="fill" size={26} className="text-fuchsia-600"/> Bulk Walk
          </h1>
          <div className="text-xs text-slate-600 mt-1 leading-snug">
            Tap <b>Camera</b> to snap photos one at a time (guaranteed to work) or <b>Gallery</b> to multi-select. Each photo stacks in the tray below the unit. When you're ready, tap the green <b>Process N</b> button to send them all to Claude at once.
            <br/>
            <span className="text-[10px] text-slate-500">Tap any checklist chip to override AI (cycles pass → fail → pending). Hot water passes at ≥99°F.</span>
          </div>
        </div>

        {/* Summary */}
        {summary && (
          <div className="grid grid-cols-3 gap-2" data-testid="walk-summary">
            <div className="p-3 bg-white border-2 border-slate-500">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Total Units</div>
              <div className="font-display font-black text-2xl">{summary.total}</div>
            </div>
            <div className="p-3 bg-white border-2 border-emerald-500">
              <div className="text-[9px] font-black uppercase tracking-widest text-emerald-700">Walked</div>
              <div className="font-display font-black text-2xl text-emerald-700">{summary.walked}</div>
            </div>
            <div className="p-3 bg-white border-2 border-fuchsia-500">
              <div className="text-[9px] font-black uppercase tracking-widest text-fuchsia-700">Appliances</div>
              <div className="font-display font-black text-2xl text-fuchsia-700">{summary.appliances}</div>
            </div>
          </div>
        )}

        {/* Filter row */}
        <div className="flex items-center gap-2 flex-wrap">
          <input value={filter} onChange={e => setFilter(e.target.value)}
            placeholder="Filter unit (e.g. 8A)"
            data-testid="walk-unit-filter"
            className="h-10 px-3 border-2 border-slate-300 text-sm flex-1 min-w-[120px]"/>
          <label className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-600 cursor-pointer">
            <input type="checkbox" checked={showComplete} onChange={e => setShowComplete(e.target.checked)}
              data-testid="walk-show-complete"/>
            Show walked
          </label>
        </div>

        {/* Unit list */}
        <div className="space-y-3" data-testid="walk-unit-list">
          {units === null && <div className="text-xs text-slate-500 py-6 text-center">Loading units…</div>}
          {units !== null && visible.length === 0 && (
            <div className="text-xs text-slate-500 py-6 text-center">No units match.</div>
          )}
          {visible.map(u => {
            const rep = reports[u.unit];
            const walking = walkingUnit === u.unit;
            const walked = !!rep;
            const border = walked ? "border-emerald-500" : "border-slate-300";
            return (
              <div key={u.id || u.unit} className={`p-3 bg-white border-2 ${border}`}
                data-testid={`walk-unit-row-${u.unit}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/admin/properties/${encodeURIComponent(address)}/unit/${encodeURIComponent(u.unit)}`}
                        className="font-display font-black text-xl hover:text-fuchsia-700">
                        Unit {u.unit}
                      </Link>
                      {u.appliance_count > 0 && (
                        <span className="text-[9px] font-black uppercase tracking-widest bg-fuchsia-600 text-white px-1.5 py-0.5">
                          {u.appliance_count} asset{u.appliance_count === 1 ? "" : "s"}
                        </span>
                      )}
                      {u.vacancy_status && (
                        <span className="text-[9px] font-black uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-500 px-1.5 py-0.5">
                          {(u.vacancy_status || "").replace("_", " ")}
                        </span>
                      )}
                      {rep && rep.hot_water_temp_f && (
                        <span className="text-[10px] font-black uppercase tracking-widest bg-cyan-600 text-white px-1.5 py-0.5 inline-flex items-center gap-1">
                          <Thermometer weight="fill" size={11}/> {rep.hot_water_temp_f}°F
                        </span>
                      )}
                    </div>
                  </div>
                  <label
                    onClick={() => toast.info(`${u.unit}: opening photo picker…`,
                                              { id: `pick-${u.unit}`, duration: 2500 })}
                    className={`relative overflow-hidden inline-flex items-center gap-1.5 h-11 px-3 font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] ${
                    walking ? "bg-fuchsia-400 text-white"
                            : "bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                  } ${walkingUnit && !walking ? "opacity-40 pointer-events-none" : ""}`}>
                    {walking
                      ? <><Sparkle weight="fill" size={13} className="animate-pulse"/> Claude…</>
                      : <><Camera weight="fill" size={13}/> Gallery</>}
                    <input type="file"
                      accept="image/*,image/heic,image/heif,.heic,.heif,.jpg,.jpeg,.png,.webp"
                      multiple
                      disabled={!!walkingUnit}
                      data-testid={`walk-scan-input-${u.unit}`}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      onChange={e => {
                        const fs = e.target.files;
                        const n = fs ? fs.length : 0;
                        console.log("[walk] gallery onChange", { unit: u.unit, count: n });
                        if (!n) {
                          toast.error(`${u.unit}: 0 photos — try Camera button instead`,
                                       { id: `pick-${u.unit}`, duration: 5000 });
                          e.target.value = "";
                          return;
                        }
                        const added = addToQueue(u.unit, fs);
                        e.target.value = "";
                        toast.success(`${u.unit}: added ${added} to tray (${(queueFor(u.unit).length + added > 15 ? 15 : queueFor(u.unit).length + added)} queued)`,
                                       { id: `pick-${u.unit}`, duration: 2500 });
                      }}/>
                  </label>
                  {/* Camera-only — capture="environment" opens the phone camera
                      directly and always returns exactly 1 photo. Guaranteed
                      to work on every Android/iOS device. Photo is added to
                      the per-unit tray; user can tap this repeatedly. */}
                  <label
                    onClick={() => toast.info(`${u.unit}: opening camera…`,
                                              { id: `cam-${u.unit}`, duration: 2000 })}
                    className={`relative overflow-hidden inline-flex items-center gap-1.5 h-11 px-3 font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] bg-cyan-600 hover:bg-cyan-700 text-white ${walkingUnit ? "opacity-40 pointer-events-none" : ""}`}>
                    <Camera weight="fill" size={13}/> Camera
                    <input type="file" accept="image/*" capture="environment"
                      disabled={!!walkingUnit}
                      data-testid={`walk-camera-input-${u.unit}`}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      onChange={e => {
                        const fs = e.target.files;
                        const n = fs ? fs.length : 0;
                        console.log("[walk] camera onChange", { unit: u.unit, count: n });
                        if (!n) {
                          toast.error(`${u.unit}: no photo captured`,
                                       { id: `cam-${u.unit}`, duration: 3000 });
                          e.target.value = "";
                          return;
                        }
                        addToQueue(u.unit, fs);
                        e.target.value = "";
                        toast.success(`${u.unit}: photo added (${queueFor(u.unit).length + 1} queued)`,
                                       { id: `cam-${u.unit}`, duration: 2000 });
                      }}/>
                  </label>
                  {walked && rep?.id && (
                    <>
                    <button onClick={() => setGalleryUnit(u.unit)}
                      data-testid={`walk-view-photos-${u.unit}`}
                      className="inline-flex items-center gap-1 h-11 px-3 bg-purple-700 hover:bg-purple-800 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a]">
                      <Images weight="fill" size={13}/> Photos ({(rep.photos || []).length || rep.total_photos || 0})
                    </button>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/walk-reports/${rep.id}/pdf?auth=${encodeURIComponent(authToken)}`}
                      target="_blank" rel="noreferrer"
                      data-testid={`walk-pdf-btn-${u.unit}`}
                      className="inline-flex items-center gap-1 h-11 px-3 bg-cyan-700 hover:bg-cyan-800 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a]">
                      <FilePdf weight="fill" size={13}/> PDF
                    </a>
                    </>
                  )}
                </div>
                {/* Photo tray — thumbnails of queued photos + Process button.
                    Renders only when the user has queued at least one photo
                    for this unit. */}
                {queueFor(u.unit).length > 0 && (
                  <div className="mt-3 p-2 bg-slate-50 border-2 border-slate-300" data-testid={`walk-tray-${u.unit}`}>
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                        {queueFor(u.unit).length} photo{queueFor(u.unit).length === 1 ? "" : "s"} ready
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => clearQueue(u.unit)} disabled={!!walkingUnit}
                          data-testid={`walk-clear-${u.unit}`}
                          className="h-9 px-2.5 bg-slate-200 hover:bg-slate-300 disabled:opacity-50 text-slate-700 font-black uppercase text-[10px] tracking-widest border-2 border-slate-500">
                          Clear
                        </button>
                        <button onClick={() => walk(u.unit, queueFor(u.unit))} disabled={!!walkingUnit}
                          data-testid={`walk-process-${u.unit}`}
                          className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-400 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1">
                          <Play weight="fill" size={12}/> Process {queueFor(u.unit).length}
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-1.5 overflow-x-auto pb-1">
                      {queueFor(u.unit).map((entry, i) => (
                        <div key={entry.id || i} className="relative flex-shrink-0"
                          data-testid={`walk-thumb-${u.unit}-${i}`}>
                          <img src={entry.url}
                            alt=""
                            className="w-16 h-16 object-cover border-2 border-slate-400"/>
                          <button onClick={() => removeFromQueue(u.unit, i)}
                            disabled={!!walkingUnit}
                            data-testid={`walk-remove-${u.unit}-${i}`}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-600 hover:bg-red-700 text-white border-2 border-white rounded-full inline-flex items-center justify-center">
                            <X weight="bold" size={10}/>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Checklist row — 8 chips, always rendered */}
                {/* Checklist row — 8 chips. Tap any chip to cycle status
                    (pending → pass → fail → pending). Small human icon
                    appears when overridden so you can see AI vs manual. */}
                <div className="mt-2 grid grid-cols-4 sm:grid-cols-8 gap-1.5" data-testid={`walk-checklist-${u.unit}`}>
                  {CHECKLIST.map(item => {
                    const c = (rep?.checklist || []).find(x => x.key === item.key);
                    const status = c?.status || "pending";
                    const overridden = !!c?.overridden_by;
                    const Icon = status === "pass" ? CheckCircle : status === "fail" ? XCircle : Circle;
                    const cls = status === "pass" ? "bg-emerald-100 border-emerald-500 text-emerald-800 hover:bg-emerald-200"
                              : status === "fail" ? "bg-red-100 border-red-500 text-red-800 hover:bg-red-200"
                              : "bg-slate-50 border-slate-300 text-slate-400 hover:bg-slate-100";
                    return (
                      <button key={item.key}
                        title={(c?.notes || item.label) + (overridden ? " · manual override" : "") + " · tap to cycle · hold for note"}
                        onPointerDown={() => {
                          pressFired.current = false;
                          pressTimer.current = setTimeout(() => {
                            pressFired.current = true;
                            setNoteEditor({ unit: u.unit, key: item.key, draft: c?.notes || "" });
                          }, 500);
                        }}
                        onPointerUp={() => { if (pressTimer.current) clearTimeout(pressTimer.current); }}
                        onPointerLeave={() => { if (pressTimer.current) clearTimeout(pressTimer.current); }}
                        onClick={() => { if (!pressFired.current) overrideCheck(u.unit, item.key); }}
                        className={`p-1.5 border ${cls} text-center cursor-pointer transition relative select-none`}
                        data-testid={`walk-check-${u.unit}-${item.key}`}>
                        <Icon weight={status === "pending" ? "regular" : "fill"} size={14} className="inline mb-0.5"/>
                        {overridden && (
                          <span className="absolute top-0 right-0.5 text-[8px] font-black text-fuchsia-700" title="manual override">✋</span>
                        )}
                        {c?.notes && (
                          <span className="absolute bottom-0.5 right-0.5 text-[8px] font-black text-blue-700" title={c.notes}>📝</span>
                        )}
                        <div className="text-[9px] font-black uppercase tracking-widest leading-none">
                          <span className="sm:hidden">{item.short}</span>
                          <span className="hidden sm:inline">{item.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {/* Long-press note editor — shows just below the chip grid when
                    a chip's editor is open for this unit. */}
                {noteEditor && noteEditor.unit === u.unit && (
                  <div className="mt-2 p-2 bg-blue-50 border-2 border-blue-500" data-testid={`note-editor-${u.unit}`}>
                    <div className="text-[10px] font-black uppercase tracking-widest text-blue-800 mb-1">
                      Note for {CHECKLIST.find(k => k.key === noteEditor.key)?.label}
                    </div>
                    <textarea value={noteEditor.draft}
                      onChange={e => setNoteEditor({...noteEditor, draft: e.target.value})}
                      placeholder='e.g. "Manually confirmed via video call with tenant"'
                      data-testid={`note-input-${u.unit}-${noteEditor.key}`}
                      rows={2}
                      className="w-full px-2 py-1 text-xs border-2 border-slate-300 resize-none"/>
                    <div className="flex gap-2 mt-1">
                      <button onClick={() => setNoteEditor(null)}
                        className="h-8 px-2 border-2 border-slate-400 font-black uppercase text-[10px] tracking-widest">Cancel</button>
                      <button onClick={async () => {
                        const ed = noteEditor;
                        setNoteEditor(null);
                        await overrideCheck(ed.unit, ed.key, { noteOnly: true, notes: ed.draft });
                      }} data-testid={`note-save-${u.unit}-${noteEditor.key}`}
                        className="h-8 px-3 bg-blue-600 text-white font-black uppercase text-[10px] tracking-widest">Save Note</button>
                    </div>
                  </div>
                )}
                {walking && progress.total > 0 && (
                  <div className="mt-2 text-[10px] text-fuchsia-700 font-black uppercase tracking-widest">
                    Analyzing {progress.total} photo{progress.total === 1 ? "" : "s"} · this may take 20-45s
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Photo viewer modal — shows every photo from the selected unit's
            walk_report with category label + Claude's short note. Tapping
            a thumbnail opens the full-size photo in a new tab. */}
        {galleryUnit && (() => {
          const rep = reports[galleryUnit];
          const photos = (rep?.photos || []);
          return (
            <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
              onClick={() => setGalleryUnit("")}
              data-testid={`walk-gallery-${galleryUnit}`}>
              <div className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}>
                <div className="p-3 border-b-2 border-[#0f172a] bg-slate-100 flex items-center justify-between sticky top-0">
                  <div>
                    <div className="font-display font-black text-xl">Unit {galleryUnit} · {photos.length} photo{photos.length === 1 ? "" : "s"}</div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      Walked {(rep?.walked_at || "").slice(0, 10)} by {rep?.walked_by || "unknown"}
                    </div>
                  </div>
                  <button onClick={() => setGalleryUnit("")}
                    data-testid="walk-gallery-close"
                    className="h-9 w-9 bg-red-600 hover:bg-red-700 text-white border-2 border-[#0f172a] inline-flex items-center justify-center">
                    <X weight="bold" size={16}/>
                  </button>
                </div>
                <div className="p-3">
                  {photos.length === 0 && (
                    <div className="text-center text-slate-500 text-sm py-8">No photos on this walk</div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {photos.map((p, i) => (
                      <div key={i} className="bg-slate-50 border-2 border-slate-300"
                        data-testid={`walk-gallery-photo-${i}`}>
                        <a href={photoUrl(p.path)} target="_blank" rel="noreferrer">
                          <img src={photoUrl(p.path)} alt=""
                            className="w-full h-40 object-cover"/>
                        </a>
                        <div className="p-1.5">
                          <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700">
                            {(p.category || "other").replace(/_/g, " ")}
                          </div>
                          {p.notes && (
                            <div className="text-[10px] text-slate-600 leading-tight mt-0.5 line-clamp-3">{p.notes}</div>
                          )}
                          {(p.flags || []).length > 0 && (
                            <div className="text-[9px] text-red-700 font-black mt-0.5">
                              ⚠ {p.flags.join(" · ")}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </AdminShell>
  );
}
